import { useState } from "react";
import GridShape from "../../components/common/GridShape";
import Input from "../../components/form/input/InputField";
import Label from "../../components/form/Label";
import { Link, useNavigate } from "react-router";
import { EyeCloseIcon, EyeIcon } from "../../icons";
import Checkbox from "../../components/form/input/Checkbox";
import Button from "../../components/ui/button/Button";
import PageMeta from "../../components/common/PageMeta";
import RegisterLoader from "../../components/common/RegisterLoader";
import { loginUser } from "../../services/authService";
import { useLanguage } from "../../context/LanguageContext";
import { getLocalizedFallback, localizeApiMessage } from "../../utils/apiMessageI18n";
import { useAuthUser } from "../../context/AuthUserContext";

export default function SignIn() {
  const { language } = useLanguage();
  const { refreshUser } = useAuthUser();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const canSubmit =
    email.trim().length > 0 && password.trim().length > 0 && !isSubmitting;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const response = await loginUser({
        email: email.trim(),
        password,
        remember_me: isChecked,
      });

      const accessToken = response.data?.access_token;
      if (!accessToken) {
        throw new Error(
          getLocalizedFallback(language, {
            pt: "Nao foi possivel realizar o login.",
            es: "No fue posible iniciar sesion.",
            en: "Could not sign in.",
          })
        );
      }

      if (response.data?.mfa_required) {
        localStorage.setItem("mfa_access_token", accessToken);
        localStorage.setItem("mfa_expires_in", String(response.data?.expires_in ?? ""));
        navigate("/twofactor-verify");
        return;
      }

      localStorage.removeItem("mfa_access_token");
      localStorage.removeItem("mfa_expires_in");
      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("token_type", response.data?.token_type || "Bearer");
      localStorage.setItem("expires_in", String(response.data?.expires_in ?? ""));
      await refreshUser();
      navigate("/");
    } catch (error) {
      const fallback = getLocalizedFallback(language, {
        pt: "Email ou senha invalidos.",
        es: "Correo o contrasena invalidos.",
        en: "Invalid email or password.",
      });
      const rawMessage = error instanceof Error ? error.message : "";
      const localizedMessage = localizeApiMessage(rawMessage, language) || fallback;
      setErrorMessage(localizedMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageMeta
        title="SignIn Dashboard | TailAdmin - Next.js Admin Dashboard Template"
        description="This is SignIn Tables Dashboard page for TailAdmin - Tailwind CSS Admin Dashboard Template"
      />
      {isSubmitting && <RegisterLoader />}
      <div className="relative flex w-full h-screen px-4 py-6 overflow-hidden bg-white z-1 dark:bg-gray-900 sm:p-0">
        <div className="flex flex-col flex-1 p-6 rounded-2xl sm:rounded-none sm:border-0 sm:p-8">
          <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
            <div>
              <div className="mb-5 sm:mb-8">
                <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                  Sign In
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Enter your email and password to sign in!
                </p>
              </div>
              <div>
                <form onSubmit={handleSubmit}>
                  <div className="space-y-6">
                    <div>
                      <Label>
                        Email <span className="text-error-500">*</span>{" "}
                      </Label>
                      <Input
                        type="email"
                        placeholder="info@gmail.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>
                        Password <span className="text-error-500">*</span>{" "}
                      </Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <span
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                        >
                          {showPassword ? (
                            <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
                          ) : (
                            <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={isChecked} onChange={setIsChecked} />
                        <span className="block font-normal text-gray-700 text-theme-sm dark:text-gray-400">
                          Keep me logged in
                        </span>
                      </div>
                      <Link
                        to="/"
                        className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    {errorMessage && (
                      <p className="text-sm text-error-500" role="alert">
                        {errorMessage}
                      </p>
                    )}
                    <div>
                      <Button className="w-full" size="sm" disabled={!canSubmit}>
                        Sign in
                      </Button>
                    </div>
                  </div>
                </form>

                <div className="mt-5">
                  <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
                    Don't have an account? {""}
                    <Link
                      to="/signup"
                      className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                    >
                      Sign Up
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="relative items-center justify-center flex-1 hidden p-8 z-1 bg-brand-950 dark:bg-white/5 lg:flex">
          {/* <!-- ===== Common Grid Shape Start ===== --> */}
          <GridShape />
          <div className="flex flex-col items-center max-w-lg">
            <div className="block mb-4">
              <img
                src="./images/logo/auth-logo.svg"
                alt="Logo"
                className="w-[520px] max-w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
